/**
 * The last identity the server confirmed, remembered on this device.
 *
 * ## The problem this exists to solve
 *
 * The workspace store picks its IndexedDB record by **user id**
 * (`snapshotKeyFor` in `store/persistence.ts`): a signed-in brewer's recipes
 * live under `snapshot.vN.<userId>`, and the signed-out workspace lives under
 * the bare `snapshot.vN`. That separation is deliberate and load-bearing — it is
 * what makes "signing in cannot overwrite guest data" a storage property rather
 * than a promise (see `store/identity.test.tsx`).
 *
 * It also means an app that does not know **who** it is opening for opens the
 * *wrong* workspace. Before this module, `fetchSession` answered "signed out"
 * for an unreachable `/api/me`, so a boot with no network opened the guest
 * record: an empty screen, for a user with a hundred recipes sitting on disk
 * three keys away. With the service worker (see `vite.config.ts`) making an
 * offline reload possible at all, that stopped being theoretical — and it was
 * already reachable in production on a slow network, where the 4 s
 * {@link AUTH_TIMEOUT_MS} can fire against a server that is merely late.
 *
 * So the identity is cached, and an unreachable `/api/me` boots as whoever was
 * last confirmed, in the {@link SessionState.status} `offline`.
 *
 * ## Security posture: this is a display and routing hint, nothing more
 *
 * The cache grants **no access to anything**. It decides which IndexedDB record
 * to open and which email to draw in the sidebar. Every interaction with the
 * server still authenticates with the `HttpOnly` session cookie, which is the
 * one and only credential and is not (and cannot be) read from here — nothing
 * in this module is a token, and forging its contents by hand gets you a UI that
 * says someone else's name over a workspace you already had read access to,
 * because it is already in IndexedDB on the same disk under the same origin.
 * An attacker holding the device does not need this file's help.
 *
 * The corollary is the rule the writers below follow: the cache is written only
 * from an answer the server actually gave, and it is cleared the moment the
 * server says the session is over — an authoritative 401, a `user: null`, a
 * sign-out, or a deleted account. See `client.ts`, which owns those calls.
 *
 * ## No expiry, on purpose
 *
 * There is no TTL. A TTL would mean that a long enough offline stretch drops the
 * brewer onto the guest workspace — precisely the bug this module removes, just
 * with a delay on it. The server is the only authority on whether a session is
 * still alive, and the moment it can be reached it is asked (`SessionProvider`
 * revalidates on `online`). Until then the honest answer is "this is who you
 * were", and the workspace it opens is the brewer's own.
 *
 * `localStorage` rather than IndexedDB because this is read **synchronously on
 * the boot path** by nothing more complicated than a JSON parse, and because a
 * few hundred bytes of identity next to `beerwright.theme` is where the other
 * boot-time hint on this origin already lives.
 */

import type { Entitlement } from '@/lib/plan';
import { parseEntitlement } from '@/lib/plan';
import type { AuthUser } from './client';

/**
 * Where the hint is kept.
 *
 * Versioned, so a future change to the shape reads as "nothing cached" — an
 * offline boot onto the guest workspace once, rather than a crash or a
 * half-parsed user.
 */
export const IDENTITY_CACHE_KEY = 'beerwright.identity.v1';

/** The last confirmed answer from `/api/me`, as it was stored. */
export interface CachedIdentity {
  readonly user: AuthUser;
  /**
   * The plan the server last granted.
   *
   * Cached alongside the user because the two arrive together and are wanted
   * together: the sidebar's recipe meter and the settings plan row are display,
   * and the limits they describe are enforced server-side at apply time
   * (`worker/entitlement.ts`). A stale copy can therefore promise nothing —
   * the worst it can do is name last week's tier until `/api/me` answers again.
   */
  readonly entitlement: Entitlement;
  /** Wall clock at the confirming `/api/me`. Diagnostic; nothing expires on it. */
  readonly confirmedAt: number;
}

/**
 * The cached identity, or `null` when there is none.
 *
 * Never throws, and validates rather than trusts: the value is JSON in
 * `localStorage`, which is to say it is user-editable, and every field it feeds
 * is a string the UI renders or a key the store opens. `id` and `email` are the
 * two that must be present and non-empty — an identity with no id cannot key a
 * workspace, so it is not an identity.
 */
export function readCachedIdentity(): CachedIdentity | null {
  const raw = readStorage();
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record = parsed as { user?: unknown; entitlement?: unknown; confirmedAt?: unknown };
  const user = parseUser(record.user);
  if (user === null) return null;
  return {
    user,
    entitlement: parseEntitlement(record.entitlement),
    confirmedAt: typeof record.confirmedAt === 'number' ? record.confirmedAt : 0,
  };
}

/** Remember this identity. Called on every `/api/me` that names a user. */
export function writeCachedIdentity(user: AuthUser, entitlement: Entitlement): void {
  const payload: CachedIdentity = { user, entitlement, confirmedAt: Date.now() };
  try {
    globalThis.localStorage?.setItem(IDENTITY_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* Private mode, or a full quota. An app that boots online is unaffected. */
  }
}

/**
 * Forget the identity.
 *
 * Called from exactly the places the server has ended the session: a 401, a
 * `user: null`, `signOut`, and `deleteAccount`. Anything else — a timeout, a
 * 503, a captive portal — must **not** reach this function, because "I could
 * not ask" is not an answer.
 */
export function clearCachedIdentity(): void {
  try {
    globalThis.localStorage?.removeItem(IDENTITY_CACHE_KEY);
  } catch {
    /* Nothing to lose: unreadable storage is also unwritable storage. */
  }
}

function readStorage(): string | null {
  try {
    return globalThis.localStorage?.getItem(IDENTITY_CACHE_KEY) ?? null;
  } catch {
    // Safari in private mode, and any browser with storage blocked for the
    // origin, throws on access rather than returning null.
    return null;
  }
}

function parseUser(value: unknown): AuthUser | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as {
    id?: unknown;
    email?: unknown;
    name?: unknown;
    image?: unknown;
    role?: unknown;
  };
  if (typeof record.id !== 'string' || record.id.length === 0) return null;
  if (typeof record.email !== 'string' || record.email.length === 0) return null;
  return {
    id: record.id,
    email: record.email,
    name: typeof record.name === 'string' ? record.name : '',
    image: typeof record.image === 'string' ? record.image : null,
    // Kept because the sidebar *offers* the ops console on it, and a signed-in
    // admin who reloads offline should not lose the link. It authorizes nothing:
    // every `/api/admin/*` request is checked against the session's own role on
    // the server, which is the boundary (see `client.ts`'s `AuthUser`).
    ...(typeof record.role === 'string' ? { role: record.role } : {}),
  };
}
